import { colors } from '../../../theme/colors'

/** Standard chart height in pixels (fixed; width is responsive). Override per-chart when needed. */
export const CHART_HEIGHT = 300

/** Height (px) of the X-axis label row `RechartsWrapper` renders below the plot. */
export const CHART_X_AXIS_LABEL_ROW_HEIGHT = 24

/** Gap (px) between the plot and the X-axis label row. */
export const CHART_X_AXIS_LABEL_ROW_GAP = 8

/** Floor (px) for the plot area after X-axis label chrome is subtracted from the outer height. */
export const CHART_MIN_PLOT_AREA_HEIGHT = 120

/** Homepage FCL snapshot charts (Figma / legacy SnapshotChartCard). */
export const SNAPSHOT_CHART_HEIGHT = 232

/** Gap between vertical Y-axis label and Recharts Y-axis tick column (px). */
export const SNAPSHOT_Y_AXIS_LABEL_GAP = '8px'

/** Reserved width for numeric Y-axis ticks on snapshot charts (Figma). */
export const SNAPSHOT_Y_AXIS_WIDTH = 38

/** Recharts chart margin on homepage snapshot charts. */
export const SNAPSHOT_CHART_MARGIN = {
  top: 8,
  right: 8,
  left: 0,
  bottom: 4,
} as const

/** Gap between chart title and plot (Figma / SnapshotChartCard). */
export const SNAPSHOT_CHART_TITLE_CONTENT_GAP = '16px'

/** FCL snapshot chart header link label (Figma). */
export const SNAPSHOT_VIEW_IN_LIST_LABEL = 'View in List'

/** Horizontal inset for snapshot chart titles inside the card (Figma). */
export const SNAPSHOT_CHART_TITLE_PADDING_LEFT = '6px'

/** Recharts Y-axis value ticks on homepage snapshot charts (Figma). */
export const SNAPSHOT_AXIS_TICK_Y = {
  fill: colors.presets.neutral[0],
  fontSize: 10,
  fontWeight: 600,
  fontFamily: 'Montserrat, sans-serif',
} as const

/** Recharts X-axis category ticks on homepage snapshot line chart (Figma: Medium). */
export const SNAPSHOT_AXIS_TICK_X = {
  fill: colors.presets.neutral[0],
  fontSize: 10,
  fontWeight: 500,
  fontFamily: 'Montserrat, sans-serif',
} as const

/** Default snapshot vertical bar width (Figma transshipment / out-for-delivery: 29px). */
export const SNAPSHOT_VERTICAL_BAR_SIZE = 29

/** Predicted Late by Carrier snapshot bar width (Figma: 65px). */
export const SNAPSHOT_VERTICAL_BAR_SIZE_CARRIER = 65

/**
 * Recharts `BarChart.barCategoryGap` for snapshot vertical bars (% of each category band).
 * Controls bar-to-bar spacing. ~24% balances Figma airy gaps without overly thin bars (34% was too much).
 * @see recharts `getBarPosition` in ChartUtils.js
 */
export const SNAPSHOT_VERTICAL_BAR_CATEGORY_GAP = '24%'

/** X-axis padding per side on snapshot vertical bar charts (px). */
export const SNAPSHOT_VERTICAL_BAR_AXIS_PADDING_PX = 8

/** Horizontal chart Y-axis tick label height (px). */
export const HORIZONTAL_CHART_Y_AXIS_LABEL_HEIGHT = 12

/** Vertical gap (px) between horizontal chart Y-axis tick labels. */
export const HORIZONTAL_CHART_Y_AXIS_LABEL_GAP = 16

/** Minimum number of rows used for horizontal chart height calculations. */
export const HORIZONTAL_CHART_MIN_ROW_COUNT = 2

/** Horizontal chart X-axis band height (px) reserved for numeric value ticks. */
export const HORIZONTAL_CHART_X_AXIS_HEIGHT = 28

/** Recharts top margin (px) reserving space for the horizontal chart's built-in legend. */
export const HORIZONTAL_CHART_LEGEND_MARGIN_TOP = 8

/** Minimum width for chart title/header content before wrapping under legends. */
export const CHART_TITLE_MIN_WIDTH = 260

/** Per-variant text styles for ChartTitle. */
export const CHART_TITLE_VARIANT_STYLES = {
  default: {
    textColor: 'white',
    fontWeight: 700,
    lineHeight: '1.4em',
  },
  snapshot: {
    textColor: 'presets.neutral.0',
    fontWeight: 600,
    lineHeight: 'normal',
    fontFamily: `'Montserrat', sans-serif`,
    whiteSpace: 'nowrap' as const,
  },
} as const

/** Minimum width (px) for the chart container so it never collapses to zero. */
export const CHART_MIN_WIDTH = 300

/** Minimum width per data point (bar group / line tick) for horizontal scroll calculation. */
export const MIN_WIDTH_PER_ITEM = 100

/** Line chart: vertical gap (px) between the plot bottom and X-axis category labels (`XAxis` `tickMargin`). */
export const LINE_CHART_X_AXIS_TICK_MARGIN = 8

/** Default line chart X-axis layout. */
export const DEFAULT_LINE_X_AXIS = {
  angle: 0,
  textAnchor: 'start' as const,
  padding: { left: 16, right: 16 },
  tickMargin: LINE_CHART_X_AXIS_TICK_MARGIN,
} as const

/** Snapshot angled category labels (−30°, Figma desktop). */
export const SNAPSHOT_ANGLED_X_AXIS = {
  angle: -30,
  dy: 0,
  textAnchor: 'end' as const,
  tickMargin: 6,
} as const

/** Inset first/last ticks so −30° labels fit; higher values tighten inter-label spacing. */
export const SNAPSHOT_LINE_ANGLED_X_AXIS_PADDING = {
  left: 28,
  right: 16,
} as const

/** Vertical offset below tick anchor for angled snapshot labels. */
export const SNAPSHOT_ANGLED_TICK_DY = 4

/** X-axis band for single-line angled labels (line chart dates). */
export const SNAPSHOT_ANGLED_X_AXIS_HEIGHT = 34

/** X-axis band for medium single-line angled labels (e.g. carrier names). */
export const SNAPSHOT_ANGLED_X_AXIS_HEIGHT_MEDIUM = 56

/** X-axis band for longer angled category labels (e.g. port / destination names). */
export const SNAPSHOT_ANGLED_X_AXIS_HEIGHT_TALL = 74

/** @deprecated Use `SNAPSHOT_ANGLED_X_AXIS_HEIGHT_TALL`. */
export const SNAPSHOT_ANGLED_X_AXIS_HEIGHT_MULTILINE =
  SNAPSHOT_ANGLED_X_AXIS_HEIGHT_TALL

/** Snapshot vertical bar angled X-axis band — set per chart in view config when needed. */
export type SnapshotAngledAxisBand = 'medium' | 'tall'

export const SNAPSHOT_ANGLED_AXIS_BAND_HEIGHT: Record<
  SnapshotAngledAxisBand,
  number
> = {
  medium: SNAPSHOT_ANGLED_X_AXIS_HEIGHT_MEDIUM,
  tall: SNAPSHOT_ANGLED_X_AXIS_HEIGHT_TALL,
}

/** Default bar thickness (px) for horizontal bar charts. */
export const HORIZONTAL_BAR_SIZE = 11

/**
 * Horizontal bar chart: extra row height per category (px), i.e. gap between category rows.
 * Recharts centers `barSize` in each band; band height = `barSize + this` ⇒ (gap/2) padding above and below the bar so Y ticks line up.
 */
export const HORIZONTAL_BAR_CATEGORY_GAP = 16

/**
 * Target horizontal gap between category columns (px). Applied via `XAxis` padding — **not**
 * as numeric `barCategoryGap` on `BarChart`, which steals space *inside* each band and forces
 * Recharts to shrink bars below the intended cluster width when bands are narrow.
 */
export const VERTICAL_BAR_INTER_CATEGORY_GAP_PX = 24

/**
 * Target width (px) of the bar cluster per category on vertical charts: one bar = full width;
 * grouped side-by-side bars split this evenly (`barGap: 0`). Stacked charts use one column = this width.
 */
export const VERTICAL_BAR_CLUSTER_WIDTH_PX = 24

/** Interior grid stroke — `colors.chart.gridStroke` (`presets.neutral.20` @ ~70%). */
export const CHART_GRID_STROKE = colors.chart.gridStroke

/** Dash pattern for interior grid segments (solid zero omits this on the 0 tick). */
export const CHART_GRID_STROKE_DASH = '2 2'

/** `strokeWidth` on custom grid `<line>` elements (Recharts default is easy to lose on custom renderers). */
export const CHART_GRID_STROKE_WIDTH = 1

/** Solid grid line at numeric value 0 — `colors.chart.gridStrokeZero`. */
export const CHART_GRID_ZERO_STROKE = colors.chart.gridStrokeZero

/** Pie chart: slices representing less than this percentage are grouped into "Other". */
export const PIE_OTHER_THRESHOLD_PCT = 2

/** Pie chart circle diameter (px). */
export const PIE_CHART_DIAMETER = 300

/** Minimum horizontal gap (px) between pie plot and legend when in one row. */
export const PIE_CHART_MIN_LEGEND_GAP = 40

/** Maximum horizontal gap (px) between pie plot and legend when in one row. */
export const PIE_CHART_MAX_LEGEND_GAP = 100

/** Standard chart color palette. Charts cycle through these when they need multiple colors. */
export const CHART_COLORS = colors.chart.palette

/** Get a color from the palette by index, cycling if needed. */
export const getChartColor = (index: number): string =>
  CHART_COLORS[index % CHART_COLORS.length]

/** 10-color palette approved for pie/donut charts. */
export const PIE_CHART_COLORS = colors.chart.piePalette

/** Background fill color for inactive/empty bar chart segments (subtle on dark backgrounds). */
export const BAR_BACKGROUND_COLOR = colors.chart.barBackground

/** Corner radius applied to the top of vertical bar charts. */
export const BAR_CORNER_RADIUS = 4

/**
 * Figma (Port-to-Port legend): node `2434:1066` “Rectangle 2482” 21×12, SVG fill `presets.turquoise/50`.
 * `Average Dwell` at x=28 → gap 28−21=**7**px. Text: `body/small/medium`.
 * Shape: `21×12` **rounded rect** (Figma reads more rectangular than a full pill). **4px** corners — not `h/2` stadium.
 * Not the MoM line (`32×2` stroke) or the plot’s horizontal data bars.
 */
export const CHART_BAR_LEGEND_SWATCH_WIDTH = '21px'
export const CHART_BAR_LEGEND_SWATCH_HEIGHT = '12px'
export const CHART_BAR_LEGEND_SWATCH_BORDER_RADIUS = '4px'
/** Use a **px string** — numeric `spacing`/`gap` on Chakra `Stack` resolves to theme keys (e.g. `7` → ~28px). */
export const CHART_BAR_LEGEND_GAP = '7px'
export const CHART_BAR_LEGEND_FONT_SIZE = '12px'
export const CHART_BAR_LEGEND_LINE_HEIGHT = '15px'
export const CHART_BAR_LEGEND_FONT_WEIGHT = 500

/**
 * Line chart header legend (MoM): horizontal **line** is `32×2` px (width × stroke); center dot sits on top.
 * `CHART_LINE_LEGEND_GAP`: icon → label (px string for Chakra `spacing`).
 */
export const CHART_LINE_LEGEND_GAP = '12px'
export const CHART_LINE_LEGEND_VIEWBOX_W = 32
export const CHART_LINE_LEGEND_VIEWBOX_H = 18
/** Line thickness (SVG `strokeWidth`) — matches Figma “2px” bar. */
export const CHART_LINE_LEGEND_LINE_STROKE_PX = 2
export const CHART_LINE_LEGEND_DOT_R_PX = 6
export const CHART_LINE_LEGEND_DOT_STROKE_PX = 3

/** Snapshot line plot dots — same ring as legend (fill = series color, white stroke). */
export const SNAPSHOT_LINE_DOT_R_PX = 5
export const SNAPSHOT_LINE_ACTIVE_DOT_R_PX = CHART_LINE_LEGEND_DOT_R_PX
export const SNAPSHOT_LINE_DOT_STROKE_PX = CHART_LINE_LEGEND_DOT_STROKE_PX

/** Figma + theme: unlading bar + legend (same as `presets.turquoise/50` / `colors.chart.barFill`). */
export const CHART_FIGMA_TEAL_UNLADING = colors.chart.barFill

/** Get a pie chart color by index, cycling through the 10-color palette. */
export const getPieChartColor = (index: number): string =>
  PIE_CHART_COLORS[index % PIE_CHART_COLORS.length]
